/**
 * Cause-to-effect recorder for the web app's reactive pipeline: store flushes, the computes they re-run, the React
 * commits and table rows those produce, and the long frames the page paid for them.
 *
 * Two tiers. Compute totals are always collected — two `performance.now()` calls a run — so the perf rollup reports
 * real numbers in every environment. Everything else is detail, collected only while debug mode is on: each burst of
 * work becomes a {@link Tick}, kept in a bounded ring and surfaced through the debug HUD, `window.__sbDebug`, and the
 * DevTools performance timeline.
 */

import { Duration } from "luxon";

/** Row counts one store flush carried for one collection. */
export type WriteCounts = {
  /** Rows inserted or replaced. */
  written: number;
  /** Rows the write left untouched because nothing in them changed. */
  unchanged: number;
  deleted: number;
};

/** One run of a store compute. */
export type ComputeRun = {
  name: string;
  /** Offset from the tick's start. */
  startMs: number;
  durationMs: number;
  /** False when the compute returned the same reference it did last time, so nothing downstream re-renders. */
  changed: boolean;
};

/** One React commit under a `<Profiler>`. */
export type RenderCommit = {
  id: string;
  phase: string;
  durationMs: number;
};

/** A frame the browser flagged as long: Long Animation Frames where supported, long tasks otherwise. */
export type LongFrame = {
  startedAt: number;
  durationMs: number;
  /** Time past the 50ms budget that blocked input; equal to `durationMs - 50` under the long-task fallback. */
  blockingMs: number;
  /** Script attribution from the Long Animation Frames API, longest first; empty under the long-task fallback. */
  scripts: { source: string; durationMs: number }[];
};

/**
 * One burst of synchronous work: everything that ran from the first store write or compute until the browser next
 * painted.
 */
export type Tick = {
  id: number;
  /** `performance.now()` at the first recorded event. */
  startedAt: number;
  /** Wall time until the frame after the burst; null while the tick is still open. */
  durationMs: number | null;
  /** What set the work off (`appsync`, `sync:item`, …); `local` when nothing named it. */
  causes: string[];
  writes: Record<string, WriteCounts>;
  computes: ComputeRun[];
  renders: RenderCommit[];
  rowsRendered: number;
  rowsSkipped: number;
  frames: LongFrame[];
};

/** Aggregate over one compute name in a {@link Summary}. */
export type ComputeSummary = {
  name: string;
  runs: number;
  /** Runs whose result was a new reference, so their consumers re-rendered. */
  changedRuns: number;
  totalMs: number;
  maxMs: number;
};

/** Aggregate over one collection's writes in a {@link Summary}. */
export type WriteSummary = WriteCounts & { collection: string; flushes: number };

/** Everything the recorder saw over a window, shaped for a person or an agent to read. */
export type Summary = {
  windowMs: number;
  /**
   * How much of the window the recorder still holds: less than `windowMs` when older ticks were dropped to stay within
   * its bound, so every total covers only this span.
   */
  coveredMs: number;
  ticks: number;
  /** Sum of tick wall times: how long the page spent reacting. */
  busyMs: number;
  computes: ComputeSummary[];
  writes: WriteSummary[];
  renders: { commits: number; totalMs: number; maxMs: number };
  rows: { rendered: number; skipped: number };
  frames: { count: number; totalMs: number; blockingMs: number; topScripts: { source: string; durationMs: number }[] };
  /** The costliest ticks in the window, whole. */
  worst: Tick[];
};

/** Always-on compute totals the perf rollup ships. */
export type ComputeTotals = { executions: number; totalDurationMs: number; maxDurationMs: number };

/** About five minutes of a busy page; past it the oldest ticks drop and a summary says how much it covers. */
const MAX_TICKS = 5_000;
const MAX_FRAMES = 500;
const WORST_TICKS = 5;
const TOP_SCRIPTS = 10;
/** A tick whose closing frame never comes (a hidden tab pauses `requestAnimationFrame`) closes after this long. */
const TICK_CLOSE_FALLBACK = Duration.fromObject({ seconds: 1 });
/** User Timing entries are cleared past this many, so the performance buffer stays bounded. */
const MAX_MEASURES = 1000;
const LONG_TASK_BUDGET = Duration.fromObject({ milliseconds: 50 });
const DEFAULT_SUMMARY_WINDOW = Duration.fromObject({ minutes: 1 });

type Listener = () => void;

/** The subset of a `long-animation-frame` entry the recorder reads; TypeScript's DOM lib does not type it yet. */
type LongAnimationFrameEntry = PerformanceEntry & {
  blockingDuration: number;
  scripts: { sourceURL: string; sourceFunctionName: string; invoker: string; duration: number }[];
};

/** The singleton behind {@link flightRecorder}; see the module docblock. */
class FlightRecorderImpl {
  private detailed = false;
  private nextTickId = 1;
  private open: Tick | null = null;
  private pendingCauses: string[] = [];
  /** The oldest instant the ticks still cover; null until a tick has been dropped. */
  private heldSince: number | null = null;
  private ticks: Tick[] = [];
  private frames: LongFrame[] = [];
  private totals: ComputeTotals = { executions: 0, totalDurationMs: 0, maxDurationMs: 0 };
  private measures = 0;
  private frameObserver: PerformanceObserver | null = null;
  private readonly listeners = new Set<Listener>();

  /** Turns per-tick detail on or off; compute totals are collected either way. */
  public setDetailed(on: boolean): void {
    if (on === this.detailed) return;
    this.detailed = on;
    if (on) {
      this.observeFrames();
    } else {
      this.frameObserver?.disconnect();
      this.frameObserver = null;
      this.open = null;
    }
  }

  /** Whether per-tick detail is being collected — the gate for the per-row counters on hot render paths. */
  public isDetailed(): boolean {
    return this.detailed;
  }

  /**
   * Names what sets off the next burst of work; call it just before the writes it explains.
   *
   * The label joins the open tick, or waits for the next one to open within the current task — writes behind an
   * `await` of in-memory work still land in it, while a label whose writes wait on the network lapses rather than
   * mislabel whatever runs next.
   */
  public note(cause: string): void {
    if (!this.detailed) return;
    if (this.open) {
      addCause(this.open, cause);
      return;
    }
    if (this.pendingCauses.length === 0) setTimeout(() => (this.pendingCauses = []), 0);
    if (!this.pendingCauses.includes(cause)) this.pendingCauses.push(cause);
  }

  /** Records one store flush's per-collection row counts. */
  public recordFlush(counts: ReadonlyMap<string, WriteCounts>): void {
    if (!this.detailed) return;
    const writes = this.currentTick().writes;
    for (const [collection, count] of counts) {
      const into = (writes[collection] ??= { written: 0, unchanged: 0, deleted: 0 });
      into.written += count.written;
      into.unchanged += count.unchanged;
      into.deleted += count.deleted;
    }
  }

  /**
   * Records one compute run into the always-on totals and, in detail mode, onto the current tick and the DevTools
   * timeline.
   *
   * @param name - The compute's label, as `useCollectionsCompute` names it.
   * @param startedAt - `performance.now()` before the run.
   * @param endedAt - `performance.now()` after it.
   * @param changed - Whether the result was a new reference.
   */
  public recordCompute(name: string, startedAt: number, endedAt: number, changed: boolean): void {
    const durationMs = endedAt - startedAt;
    this.totals.executions += 1;
    this.totals.totalDurationMs += durationMs;
    if (durationMs > this.totals.maxDurationMs) this.totals.maxDurationMs = durationMs;
    if (!this.detailed) return;

    const tick = this.currentTick();
    tick.computes.push({ name, startMs: startedAt - tick.startedAt, durationMs, changed });
    this.measure(`compute ${name}`, startedAt, endedAt);
  }

  /** Records one React commit under a `<Profiler>`. */
  public recordCommit(id: string, phase: string, durationMs: number): void {
    if (!this.detailed) return;
    this.currentTick().renders.push({ id, phase, durationMs });
  }

  /** Counts one table row that rendered (`true`) or was skipped by its memo (`false`). */
  public countRow(rendered: boolean): void {
    if (!this.detailed) return;
    const tick = this.currentTick();
    if (rendered) tick.rowsRendered += 1;
    else tick.rowsSkipped += 1;
  }

  /** The compute totals since the last {@link resetComputeTotals}, for the perf rollup. */
  public computeTotals(): ComputeTotals {
    return { ...this.totals };
  }

  /** Starts a fresh compute-totals window; the perf reporter calls it after each rollup ships. */
  public resetComputeTotals(): void {
    this.totals = { executions: 0, totalDurationMs: 0, maxDurationMs: 0 };
  }

  /** The most recent closed ticks, newest first. */
  public recentTicks(limit = 50): Tick[] {
    return this.ticks.slice(-limit).reverse();
  }

  /** Aggregates every closed tick and long frame in the last `windowMs`. */
  public summary(windowMs = DEFAULT_SUMMARY_WINDOW.toMillis()): Summary {
    const now = performance.now();
    const since = now - windowMs;
    const ticks = this.ticks.filter((tick) => tick.startedAt >= since);
    const frames = this.frames.filter((frame) => frame.startedAt >= since);
    return {
      windowMs,
      coveredMs: this.heldSince === null ? windowMs : now - Math.max(since, this.heldSince),
      ticks: ticks.length,
      busyMs: ticks.reduce((sum, tick) => sum + (tick.durationMs ?? 0), 0),
      computes: summarizeComputes(ticks),
      writes: summarizeWrites(ticks),
      renders: summarizeRenders(ticks),
      rows: {
        rendered: ticks.reduce((sum, tick) => sum + tick.rowsRendered, 0),
        skipped: ticks.reduce((sum, tick) => sum + tick.rowsSkipped, 0),
      },
      frames: summarizeFrames(frames),
      worst: [...ticks].sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0)).slice(0, WORST_TICKS),
    };
  }

  /**
   * Resets the recorder, waits `ms`, and returns the summary of exactly that window — the one call an agent driving
   * a browser needs to measure an interaction.
   */
  public async record(ms: number): Promise<Summary> {
    this.reset();
    await new Promise((resolve) => setTimeout(resolve, ms));
    return this.summary(ms);
  }

  /** Drops every recorded tick and frame. */
  public reset(): void {
    this.heldSince = null;
    this.ticks = [];
    this.frames = [];
    this.open = null;
    this.notify();
  }

  /** Subscribes to tick closes; the HUD throttles its own reads. */
  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private currentTick(): Tick {
    if (this.open) return this.open;
    const tick: Tick = {
      id: this.nextTickId++,
      startedAt: performance.now(),
      durationMs: null,
      causes: this.pendingCauses,
      writes: {},
      computes: [],
      renders: [],
      rowsRendered: 0,
      rowsSkipped: 0,
      frames: [],
    };
    this.pendingCauses = [];
    this.open = tick;
    this.scheduleClose(tick);
    return tick;
  }

  /** Closes the tick once the frame after the burst has painted, or after a fallback when frames are paused. */
  private scheduleClose(tick: Tick): void {
    const fallback = setTimeout(() => this.closeTick(tick), TICK_CLOSE_FALLBACK.toMillis());
    if (typeof requestAnimationFrame === "undefined") return;
    requestAnimationFrame(() => {
      setTimeout(() => {
        clearTimeout(fallback);
        this.closeTick(tick);
      }, 0);
    });
  }

  private closeTick(tick: Tick): void {
    if (tick.durationMs !== null || this.open !== tick) return;
    const endedAt = performance.now();
    tick.durationMs = endedAt - tick.startedAt;
    if (tick.causes.length === 0) tick.causes.push("local");
    // Frames already observed during the burst belong to it; later ones are attached as they arrive.
    tick.frames = this.frames.filter((frame) => frame.startedAt >= tick.startedAt && frame.startedAt <= endedAt);
    this.open = null;
    this.ticks.push(tick);
    if (this.ticks.length > MAX_TICKS) {
      this.ticks.splice(0, this.ticks.length - MAX_TICKS);
      this.heldSince = this.ticks[0]?.startedAt ?? endedAt;
    }
    this.measure(`tick ${tick.id} (${tick.causes.join(", ")})`, tick.startedAt, endedAt);
    this.notify();
  }

  /** Observes long frames, preferring Long Animation Frames (script attribution) over long tasks. */
  private observeFrames(): void {
    if (typeof PerformanceObserver === "undefined") return;
    const supported = PerformanceObserver.supportedEntryTypes ?? [];
    const type = supported.includes("long-animation-frame") ? "long-animation-frame" : "longtask";
    if (!supported.includes(type)) return;
    this.frameObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) this.addFrame(toLongFrame(entry));
    });
    this.frameObserver.observe({ type, buffered: false });
  }

  private addFrame(frame: LongFrame): void {
    this.frames.push(frame);
    if (this.frames.length > MAX_FRAMES) this.frames.splice(0, this.frames.length - MAX_FRAMES);
    // Frame entries are delivered after the fact, so the tick they overlap has usually closed already.
    const owner = this.ticks.findLast(
      (tick) =>
        tick.startedAt <= frame.startedAt + frame.durationMs &&
        frame.startedAt <= tick.startedAt + (tick.durationMs ?? 0),
    );
    if (owner && !owner.frames.includes(frame)) owner.frames.push(frame);
  }

  private measure(name: string, start: number, end: number): void {
    performance.measure(name, { start, end });
    this.measures += 1;
    if (this.measures < MAX_MEASURES) return;
    performance.clearMeasures();
    this.measures = 0;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

function addCause(tick: Tick, cause: string): void {
  if (!tick.causes.includes(cause)) tick.causes.push(cause);
}

function toLongFrame(entry: PerformanceEntry): LongFrame {
  if (entry.entryType !== "long-animation-frame") {
    return {
      startedAt: entry.startTime,
      durationMs: entry.duration,
      blockingMs: Math.max(0, entry.duration - LONG_TASK_BUDGET.toMillis()),
      scripts: [],
    };
  }
  const frame = entry as LongAnimationFrameEntry;
  return {
    startedAt: frame.startTime,
    durationMs: frame.duration,
    blockingMs: frame.blockingDuration,
    scripts: frame.scripts
      .map((script) => ({ source: scriptSource(script), durationMs: script.duration }))
      .sort((a, b) => b.durationMs - a.durationMs),
  };
}

/** A readable label for a LoAF script entry: the function when named, otherwise its invoker and file. */
function scriptSource(script: LongAnimationFrameEntry["scripts"][number]): string {
  const file = script.sourceURL.split("/").pop()?.split("?")[0] ?? "";
  const fn = script.sourceFunctionName || script.invoker;
  return file ? `${fn} (${file})` : fn;
}

function summarizeComputes(ticks: readonly Tick[]): ComputeSummary[] {
  const byName = new Map<string, ComputeSummary>();
  for (const run of ticks.flatMap((tick) => tick.computes)) {
    const into = byName.get(run.name) ?? { name: run.name, runs: 0, changedRuns: 0, totalMs: 0, maxMs: 0 };
    into.runs += 1;
    if (run.changed) into.changedRuns += 1;
    into.totalMs += run.durationMs;
    into.maxMs = Math.max(into.maxMs, run.durationMs);
    byName.set(run.name, into);
  }
  return [...byName.values()].sort((a, b) => b.totalMs - a.totalMs);
}

function summarizeWrites(ticks: readonly Tick[]): WriteSummary[] {
  const byCollection = new Map<string, WriteSummary>();
  for (const tick of ticks) {
    for (const [collection, count] of Object.entries(tick.writes)) {
      const into = byCollection.get(collection) ?? { collection, flushes: 0, written: 0, unchanged: 0, deleted: 0 };
      into.flushes += 1;
      into.written += count.written;
      into.unchanged += count.unchanged;
      into.deleted += count.deleted;
      byCollection.set(collection, into);
    }
  }
  return [...byCollection.values()].sort((a, b) => b.written + b.unchanged - (a.written + a.unchanged));
}

function summarizeRenders(ticks: readonly Tick[]): Summary["renders"] {
  const commits = ticks.flatMap((tick) => tick.renders);
  return {
    commits: commits.length,
    totalMs: commits.reduce((sum, commit) => sum + commit.durationMs, 0),
    maxMs: commits.reduce((max, commit) => Math.max(max, commit.durationMs), 0),
  };
}

function summarizeFrames(frames: readonly LongFrame[]): Summary["frames"] {
  const bySource = new Map<string, number>();
  for (const script of frames.flatMap((frame) => frame.scripts)) {
    bySource.set(script.source, (bySource.get(script.source) ?? 0) + script.durationMs);
  }
  return {
    count: frames.length,
    totalMs: frames.reduce((sum, frame) => sum + frame.durationMs, 0),
    blockingMs: frames.reduce((sum, frame) => sum + frame.blockingMs, 0),
    topScripts: [...bySource.entries()]
      .map(([source, durationMs]) => ({ source, durationMs }))
      .sort((a, b) => b.durationMs - a.durationMs)
      .slice(0, TOP_SCRIPTS),
  };
}

export const flightRecorder = new FlightRecorderImpl();
